// useReviewQueueBadge — poll count of pending review-queue entries for the sidebar
// nav badge.  Fires GET /api/time-entries/review-queue/count on mount + every 60s,
// and refreshes immediately on any event that could change the queue:
//   • 'review-queue-changed'  (dispatched from TimeReviewQueue after approve /
//                              request-changes / bulk mutations)
//   • 'timerEntrySaved'       (posting an entry can add to the queue when the
//                              owner has time_review_required=true)
//   • storage event for 'lextrack_timer_broadcast'  (cross-window save per the
//                              CLAUDE.md cross-window pattern)
//
// Skips fetching entirely when the caller lacks the time_entries perm — the
// backend would 403 anyway and there's no reason to add noise — and when the
// firm does not use time review at all (GET /api/auth/me reports
// time_review_configured: false — nobody active is set to require review and
// nothing is waiting in the queue).  In that state this 60 s poll — the
// API's second-busiest call, about 12% of all requests over the 24 h to
// 2026-10-12 — fetched a count that could only be 0.  The sidebar also
// leaves the queue out in that state (lib/navItems.js showInSidebar); the
// page still opens directly.  The flag is read once per page load / sign-in
// (it rides on /me), so a firm that turns review on gets the entry and the
// badge from each person's next reload.  Errors are swallowed silently: the
// badge just renders nothing on failure rather than producing a toast on
// every 60-second poll.

import { useEffect, useState, useCallback, useRef } from 'react'
import api from './../services/api'
import { useAuth } from './../context/AuthContext'
import { hasPermission, isTimeReviewConfigured } from '../lib/navItems'

export function useReviewQueueBadge ({ enabled = true } = {}) {
  const { user } = useAuth()
  const hasPerm = hasPermission(user, 'time_entries')
  // `enabled` lets a caller declaratively skip polling (e.g. the sidebar
  // gates the badge behind a feature flag and doesn't want the 60s poll
  // running when the flag is off).  AND-ed with the perm check and the
  // review-configured check so a caller passing enabled:true still
  // short-circuits when either says no.
  const effectiveEnabled = enabled && hasPerm && isTimeReviewConfigured(user)
  const [count, setCount] = useState(0)
  const debounceRef = useRef(null)
  const enabledRef = useRef(effectiveEnabled)
  enabledRef.current = effectiveEnabled

  const refresh = useCallback(async () => {
    if (!enabledRef.current) return
    try {
      const r = await api.get('/api/time-entries/review-queue/count')
      setCount(r.data?.count || 0)
    } catch {
      /* silent — badge stays at its last-known value */
    }
  }, [])

  // Debounced version — event bursts (a bulk-approve fires N events in a row
  // in some code paths) collapse into a single fetch.
  const debouncedRefresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      refresh()
    }, 150)
  }, [refresh])

  useEffect(() => {
    if (!effectiveEnabled) {
      setCount(0)
      return undefined
    }
    refresh()
    const interval = setInterval(refresh, 60000)
    const onEvent = () => debouncedRefresh()
    const onStorage = (e) => {
      if (e.key === 'lextrack_timer_broadcast') debouncedRefresh()
    }
    window.addEventListener('review-queue-changed', onEvent)
    window.addEventListener('timerEntrySaved', onEvent)
    window.addEventListener('storage', onStorage)
    return () => {
      clearInterval(interval)
      window.removeEventListener('review-queue-changed', onEvent)
      window.removeEventListener('timerEntrySaved', onEvent)
      window.removeEventListener('storage', onStorage)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [effectiveEnabled, refresh, debouncedRefresh])

  return { count, refresh }
}

export default useReviewQueueBadge
